/**
 * KanbanChecklistRow — single flat-row rendering of a Card when its
 * Lane is in `display_mode = 'checklist'` (qb!2870).
 *
 * Operator complaint 2026-05-15: *"allow the option to turn the lane
 * into checklist style cards, where you can tick once done, or x if
 * you want to archive it without marking status as done"*.
 *
 * Contract:
 *   - Tick (✓) sets `Card.status = 'DONE'`. The card stays in its
 *     current lane — only the status changes. Visual: the row gets
 *     a strikethrough + muted foreground.
 *   - Cross (✗) sets `Card.status = 'ARCHIVED'`. The status enum
 *     already exists on the BE — no migration needed for either
 *     transition. Archived cards are hidden from the board's
 *     default render by the board view's `excludeStatuses` filter.
 *   - Title is click-to-open (forwarded to `onOpen`) so the row
 *     stays consistent with `KanbanCard` for downstream wiring.
 *   - Inline rename (double-click title) is supported when
 *     `onRenameInline` is wired — same pattern as `KanbanCard`.
 *   - DRAG SOURCE: the row is `draggable={true}` so the operator can
 *     drag it between lanes (lane-reorder, handled by KanbanLane.onCardDrop)
 *     and onto another card to nest (Stack 12 MR-C, handled by
 *     KanbanCard / KanbanChecklistRow `onDropAsChild`). This was the
 *     2026-05-22 fix — previously the row was non-draggable and the
 *     operator saw "drag does nothing" on every checklist-mode lane.
 *   - DROP TARGET: the row is itself a nest-drop target so dragging
 *     Feature #6 onto Feature #7 (when both render as checklist rows)
 *     nests #6 under #7. Visual feedback is a violet ring (mirrors
 *     KanbanCard's `nestDropHover`).
 *   - RIGHT-CLICK: forwarded to `onContextMenu` so the "Make sub-card
 *     of…" / "Promote to top level" / "Archive with N sub-cards…"
 *     menu items work in checklist mode too.
 *
 * I→O decomposition (rule 1.6): no side effects, no data fetching.
 * Pure render of a single Card, plus event callbacks (tick / archive /
 * open / drag / drop / context-menu). The host owns the mutation hooks.
 */
import { useState } from 'react'
import { Check, MoreHorizontal, X as XIcon } from 'lucide-react'

import type { KanbanApi, KanbanMember } from '../api'
import type { CardSurfaceId, CardSurfaceRuntime } from '../card-surface/contract'
import type { Card as KanbanCardModel } from '../types'
import { MULTI_CARD_MIME, parseDragCardIds } from '../utils/cardSelection'
import { KanbanCardPeekHandle } from './KanbanCardPeekHandle'
import { getKanbanCardThreadSupport } from './kanbanCardThreadSupport'

const KANBAN_CARD_MIME = 'text/x-kanban-card-id'

export interface KanbanChecklistRowProps {
  card: KanbanCardModel
  /** P2 projection metadata supplied by the lane card-surface adapter. */
  cardSurfaceId?: CardSurfaceId
  cardSurfaceMode?: CardSurfaceRuntime['mode']
  api?: KanbanApi
  currentUserId?: string | number | null
  resolveMember?: (userId: number) => KanbanMember | undefined
  /** Tick → mark card DONE (status='DONE'). The host wires this to
   *  `api.updateCard(card.id, { status: 'DONE' })` via the board
   *  view's mutation hook. */
  onTickDone?: (card: KanbanCardModel) => Promise<unknown> | void
  /** Cross → archive card without marking it as done (status='ARCHIVED').
   *  Distinct from `onTickDone` because the operator's spec is
   *  explicit: "X if you want to archive it without marking status
   *  as done". */
  onArchive?: (card: KanbanCardModel) => Promise<unknown> | void
  /** Click the title row → open the card drawer (same as
   *  `KanbanCard.onOpen`). */
  onOpen?: (card: KanbanCardModel) => void
  /** Double-click → inline rename. Mirrors `KanbanCard.onRenameInline`
   *  so the row UX is consistent with tile mode. */
  onRenameInline?: (card: KanbanCardModel, nextTitle: string) => Promise<unknown> | void
  /** Right-click → open the same context menu KanbanCard fires
   *  (Archive / Move / Allocate / Make sub-card of… / Promote to top
   *  level / Archive with N sub-cards…). Parity with KanbanCard so
   *  checklist-mode lanes don't lose the menu. */
  onContextMenu?: (
    card: KanbanCardModel,
    anchor: { x: number; y: number },
  ) => void
  /** Stack 12 MR-C — nest THIS row's card under `parent` when another
   *  card is dropped onto it. Mirrors `KanbanCard.onDropAsChild`, including
   *  the optional multi-select id list so dropping a multi-selection nests
   *  the whole batch (BUG-2026-06-29 — checklist rows used to nest only the
   *  single grabbed card). */
  onDropAsChild?: (
    parent: KanbanCardModel,
    draggedCardId: string,
    draggedCardIds?: string[],
  ) => void | Promise<unknown>
  /** Drag-source hook — fired on dragstart so the host can stamp the
   *  `text/x-kanban-card-id` MIME onto the dataTransfer. Mirrors the
   *  same prop on `KanbanCard`; KanbanLane wires both identically so
   *  the wire shape is independent of `display_mode`. */
  onDragStart?: (card: KanbanCardModel, e: React.DragEvent) => void
}

export function KanbanChecklistRow({
  card,
  cardSurfaceId = 'checklist-row',
  cardSurfaceMode,
  api,
  currentUserId,
  resolveMember,
  onTickDone,
  onArchive,
  onOpen,
  onRenameInline,
  onContextMenu,
  onDropAsChild,
  onDragStart,
}: KanbanChecklistRowProps) {
  const [renaming, setRenaming] = useState(false)
  const [draft, setDraft] = useState(card.title)
  const isDone = card.status === 'DONE'
  // Pending state debounces double-clicks on the tick / archive
  // buttons — the network round-trip can take 200-500ms and we
  // don't want the operator to fire two mutations in flight.
  const [pending, setPending] = useState(false)
  // Visual feedback while another card is being dragged over THIS
  // row. Mirrors `KanbanCard.nestDropHover` (violet ring) so the
  // operator gets the same affordance regardless of display mode.
  const [nestDropHover, setNestDropHover] = useState(false)
  const threadSupport = getKanbanCardThreadSupport(card, api)

  const commit = async () => {
    const next = draft.trim()
    if (!next || next === card.title) {
      setRenaming(false)
      setDraft(card.title)
      return
    }
    try {
      await onRenameInline?.(card, next)
    } finally {
      setRenaming(false)
    }
  }

  const wrap = async (fn?: (c: KanbanCardModel) => Promise<unknown> | void) => {
    if (!fn || pending) return
    setPending(true)
    try {
      await fn(card)
    } finally {
      setPending(false)
    }
  }

  return (
    <div
      data-card-id={card.id}
      data-card-surface={cardSurfaceId}
      data-card-runtime={cardSurfaceMode}
      data-card-status={card.status}
      data-checklist-row=""
      // Drag source — make the row pick-up-able. Without this the
      // browser swallows the mousedown and the operator sees "drag
      // does nothing" (the 2026-05-22 regression).
      draggable={!renaming}
      onDragStart={(e) => {
        // Default wire: stamp the card-id MIME so nest-drop targets
        // can read it. `onDragStart` from the host overrides this
        // if it sets the same MIME (KanbanLane does — same wire as
        // the KanbanCard path).
        try {
          e.dataTransfer.setData(KANBAN_CARD_MIME, card.id)
          e.dataTransfer.effectAllowed = 'move'
        } catch {
          // Some browsers (older Safari) throw on setData outside
          // a real drag — ignore, the host wire will retry.
        }
        onDragStart?.(card, e)
      }}
      onDragOver={(e) => {
        // Nest-drop branch — accept a same-board card drag and show
        // the violet ring. Stop propagation so the lane's
        // reorder-drop handler doesn't ALSO fire (otherwise the
        // card would both nest AND move to the lane bottom).
        const types = e.dataTransfer.types
        if (onDropAsChild && types.includes(KANBAN_CARD_MIME)) {
          e.preventDefault()
          e.stopPropagation()
          e.dataTransfer.dropEffect = 'move'
          if (!nestDropHover) setNestDropHover(true)
        }
      }}
      onDragLeave={(e) => {
        // Same filter as KanbanCard — ignore child-enter events so
        // the ring doesn't strobe when the cursor crosses the
        // checkbox / title / archive sub-elements.
        if (e.currentTarget.contains(e.relatedTarget as Node | null)) return
        if (nestDropHover) setNestDropHover(false)
      }}
      onDrop={(e) => {
        if (!onDropAsChild) return
        const draggedId = e.dataTransfer.getData(KANBAN_CARD_MIME)
        if (!draggedId || draggedId === card.id) {
          if (nestDropHover) setNestDropHover(false)
          return
        }
        e.preventDefault()
        e.stopPropagation()
        setNestDropHover(false)
        // Multi-select parity with KanbanCard: a multi-selection drag stamps
        // every id under MULTI_CARD_MIME; nest the whole batch (minus this
        // row's own card) when present, else the single grabbed card.
        const ids = parseDragCardIds(
          e.dataTransfer.getData(MULTI_CARD_MIME),
          draggedId,
        ).filter((id) => id !== card.id)
        if (ids.length === 0) return
        void onDropAsChild(card, ids[0], ids.length > 1 ? ids : undefined)
      }}
      onContextMenu={(e) => {
        if (!onContextMenu) return
        // Skip when the right-click landed inside an open menu —
        // operators expect right-click to dismiss, not stack.
        const target = e.target as HTMLElement | null
        if (target?.closest?.('[role="menu"]')) return
        e.preventDefault()
        e.stopPropagation()
        onContextMenu(card, { x: e.clientX, y: e.clientY })
      }}
      className={[
        // 2026-05-22 DROP-TARGET-GAP FIX — `py-[7px] -my-[3px]`
        // replaces the previous `py-1` (4px). Without it, drops that
        // land in the inter-row gap (the lane container uses
        // `gap-1` between rows) fall through to the lane's
        // reorder handler instead of nesting. Negative margin
        // restores the visible spacing; the extra padding makes
        // the row's drop target span the full gap.
        'group/row flex items-center gap-2 rounded px-2 py-[7px] -my-[3px] text-[13px] transition-colors',
        'hover:bg-neutral-100/80 dark:hover:bg-zinc-800/60',
        isDone ? 'opacity-65' : '',
        // Cursor + ring feedback for the drag interaction.
        !renaming ? 'cursor-grab active:cursor-grabbing' : '',
        nestDropHover
          ? 'ring-2 ring-violet-500/70 ring-offset-1 bg-violet-50/60 dark:bg-violet-950/30'
          : '',
      ].join(' ')}
      style={{ color: 'var(--card-fg, currentColor)' }}
    >
      <button
        type="button"
        aria-label={isDone ? 'Mark as not done' : 'Mark as done'}
        title={isDone ? 'Mark as not done' : 'Tick — mark card DONE'}
        onClick={() => void wrap(onTickDone)}
        disabled={!onTickDone || pending}
        className={[
          'shrink-0 inline-flex h-5 w-5 items-center justify-center rounded border transition-colors',
          isDone
            ? 'border-emerald-500 bg-emerald-500 text-white'
            : 'border-neutral-300 text-transparent hover:text-emerald-600 hover:border-emerald-500 dark:border-zinc-600 dark:hover:border-emerald-400',
          (!onTickDone || pending) ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        ].join(' ')}
      >
        <Check className="h-3 w-3" strokeWidth={3} />
      </button>

      {renaming && onRenameInline ? (
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => void commit()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              void commit()
            }
            if (e.key === 'Escape') {
              setDraft(card.title)
              setRenaming(false)
            }
          }}
          className="min-w-0 flex-1 rounded bg-white px-1 py-0.5 text-[13px] text-neutral-800 dark:bg-neutral-900 dark:text-neutral-100 border border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/30"
        />
      ) : (
        <button
          type="button"
          onClick={() => onOpen?.(card)}
          onDoubleClick={() => onRenameInline && setRenaming(true)}
          className={[
            'min-w-0 flex-1 truncate text-left',
            isDone ? 'line-through' : '',
          ].join(' ')}
          title={card.title}
        >
          {card.title}
        </button>
      )}

      {cardSurfaceMode === 'interactive' && threadSupport.canShowThread && api && (
        <KanbanCardPeekHandle
          cardId={card.id}
          cardTitle={card.title}
          api={api}
          visible={false}
          threadEnabled={threadSupport.hasAiThread}
          card={card}
          resolveMember={resolveMember}
          currentUserId={currentUserId}
        />
      )}

      {cardSurfaceMode === 'interactive' && onContextMenu && (
        <button
          type="button"
          aria-label="More actions"
          title="More actions"
          onClick={(event) => {
            event.stopPropagation()
            const rect = event.currentTarget.getBoundingClientRect()
            onContextMenu(card, { x: rect.right, y: rect.bottom })
          }}
          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-100"
        >
          <MoreHorizontal className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      )}

      <button
        type="button"
        aria-label="Archive card"
        title="✕ — archive (does NOT mark as done)"
        onClick={() => void wrap(onArchive)}
        disabled={!onArchive || pending}
        className={[
          'shrink-0 inline-flex h-5 w-5 items-center justify-center rounded text-neutral-400 transition-colors',
          'opacity-0 group-hover/row:opacity-100 focus:opacity-100',
          'hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-950/50 dark:hover:text-red-400',
          (!onArchive || pending) ? 'cursor-not-allowed opacity-30' : 'cursor-pointer',
        ].join(' ')}
      >
        <XIcon className="h-3 w-3" strokeWidth={2.5} />
      </button>
    </div>
  )
}
